import wordpressMediaData from '@/data/wordpress-media.json';

export interface MediaAsset {
  id: string;
  name: string;
  origins: string[];
  fallback: string | null;
  category: string;
}

interface MediaData {
  images: MediaAsset[];
}

const mediaData: MediaData = wordpressMediaData as MediaData;

const cache = new Map<string, string>();

export const mediaService = {
  resolveMedia(idOrUrl: string): string {
    // Guard against undefined/null values
    if (!idOrUrl) {
      return '';
    }

    if (cache.has(idOrUrl)) {
      return cache.get(idOrUrl)!;
    }

    if (idOrUrl.startsWith('http://') || idOrUrl.startsWith('https://')) {
      cache.set(idOrUrl, idOrUrl);
      return idOrUrl;
    }

    const asset = mediaData.images.find(img => img.id === idOrUrl);

    if (!asset) {
      console.warn(`Media asset not found: ${idOrUrl}`);
      return idOrUrl;
    }

    const resolvedUrl = asset.origins[0] || asset.fallback || '';
    cache.set(idOrUrl, resolvedUrl);
    return resolvedUrl;
  },

  resolveMediaWithFallback(idOrUrl: string): { primary: string; fallback?: string } {
    // Guard against undefined/null values
    if (!idOrUrl) {
      return { primary: '' };
    }

    if (idOrUrl.startsWith('http://') || idOrUrl.startsWith('https://')) {
      return { primary: idOrUrl };
    }

    const asset = mediaData.images.find(img => img.id === idOrUrl);

    if (!asset) {
      // Don't warn for local paths (starting with /), they are likely intentional direct paths
      if (!idOrUrl.startsWith('/')) {
        console.warn(`Media asset not found: ${idOrUrl}`);
      }
      return { primary: idOrUrl };
    }

    return {
      primary: asset.origins[0] || '',
      fallback: asset.fallback || asset.origins[1] || undefined
    };
  },

  getAssetById(id: string): MediaAsset | undefined {
    return mediaData.images.find(img => img.id === id);
  },

  getAllAssets(): MediaAsset[] {
    return mediaData.images;
  },

  getAssetsByCategory(category: string): MediaAsset[] {
    return mediaData.images.filter(img => img.category === category);
  },

  async checkImageAvailability(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD', mode: 'no-cors' });
      return response.ok || response.type === 'opaque';
    } catch (error) {
      console.error(`Failed to check image: ${url}`, error);
      return false;
    }
  }
};

export default mediaService;
